import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { fetchAuthSession } from '@aws-amplify/auth';
import { EMPTY, expand, finalize, forkJoin, from, last, map, scan } from 'rxjs';
import { IncidentService } from 'src/app/services/api/incident.service';
import { MaterialModule } from 'src/app/material/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import Swal from 'sweetalert2';
import { DialogPreOperativeIncidentForm } from '../review-file/dialog-pre-operative-incident-form.component';
import { ConfirmDeleteObsDialogComponent } from '../confirm-delete-obs-dialog/confirm-delete-obs-dialog.component';

type IncidentStatus = 'PENDING' | 'RESOLVED' | 'APPROVED' | 'NOT_APPROVED';
interface PreOperativeIncident {
  id: string;
  itineraryId: string;
  incidentReason: string;
  description: string;
  destination: string;
  incidentDate: string;
  deadline: string;
  incidentStatus: string;
  approvedBy?: string;
}
@Component({
  selector: 'app-pre-operational-incidents-dialog',
  standalone: true,
  templateUrl: './pre-operational-incidents-dialog.component.html',
  styleUrls: ['./pre-operational-incidents-dialog.component.css'],
  imports: [CommonModule, MaterialModule, TablerIconsModule]
})
export class PreOperationalIncidentsDialogComponent implements OnInit {
  isLoading = true;
  loadingMessage = 'Cargando observaciones pre-operativas...';
  isUpdating = false;

  incidents: PreOperativeIncident[] = [];
  hasMigratedIncidents = false;
  itinerario: any;
  usuario: string = '';

  isAddingObservation = false;

  INCIDENT_STATUS = {
    PENDING: 'PENDING',
    RESOLVED: 'RESOLVED',
    APPROVED: 'APPROVED',
    NOT_APPROVED: 'NOT_APPROVED'
  } as const;

  constructor(
    private readonly incidentService: IncidentService,
    private readonly cdRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<PreOperationalIncidentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      itinerario: any;
    },
    private dialog: MatDialog
  ) {}

  private updateMigratedStatus() {
    this.hasMigratedIncidents = this.incidents.some(
      (incident) =>
        !incident.destination ||
        !incident.deadline ||
        incident.approvedBy === 'Sistema'
    );
  }
  private async loadCurrentUser(): Promise<string> {
    try {
      let user = null;
      const name = localStorage.getItem('nombre');
      const lastname = localStorage.getItem('apellido');

      console.log({ name, lastname });

      if (name && lastname) {
        return `${name} ${lastname}`;
      } else {
        console.warn(
          'Nombre o apellido no encontrados en localStorage, intentando obtener usuario actual de AWS Amplify Current User'
        );
      }

      const session = await fetchAuthSession();

      console.log({ session });

      if (session.tokens?.idToken?.payload) {
        const payload = session.tokens.idToken.payload;
        const email = (payload['email'] as string) || '';
        console.log('- email:', email);

        if (email.includes('@')) {
          user = email.split('@')[0];
        } else {
          console.warn(
            'El email del usuario no tiene un formato esperado:',
            email
          );
          user = email;
        }
      }

      console.log({ user });

      if (!user) {
        throw new Error('Usuario actual no encontrado o vacío');
      }
      return user;
    } catch (error) {
      console.error('Error al obtener usuario actual:', error);
      throw error;
    }
  }

  ngOnInit() {
    this.isLoading = true;
    this.loadingMessage = 'Cargando observaciones pre-operativas...';
    this.itinerario = this.data.itinerario;

    forkJoin({
      user: from(this.loadCurrentUser()),
      incidents: this.fetchPreOperativeIncidents({
        itineraryId: this.itinerario.id
      })
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ({ incidents, user }) => {
          console.log({ incidents, user });
          this.usuario = user;
          this.incidents = this.sortIncidents(incidents);
          this.updateMigratedStatus();
        },
        error: (err) => {
          console.error('Error initializing observaciones dialog:', err);
        }
      });
  }

  fetchPreOperativeIncidents({ itineraryId }: { itineraryId: string }) {
    return this.incidentService
      .findAllPreOperationalIncidents({
        startEvaluatedKeyId: undefined
      })
      .pipe(
        expand((response) => {
          const nextStartEvaluatedKeyId =
            response.findAllIncidenciasPreOperativas?.lastEvaluatedKeyId ??
            undefined;

          if (!nextStartEvaluatedKeyId) {
            return EMPTY;
          }

          return this.incidentService.findAllPreOperationalIncidents({
            startEvaluatedKeyId: nextStartEvaluatedKeyId
          });
        }),
        map(
          (response) =>
            response.findAllIncidenciasPreOperativas?.preOpIncidents ?? []
        ),
        scan((allIncidents, pageIncidents) => {
          const nextAllIncidents = [...allIncidents, ...pageIncidents];
          this.loadingMessage = `Cargando observaciones pre-operativas... ${nextAllIncidents.length} registros cargados`;
          return nextAllIncidents;
        }, [] as PreOperativeIncident[]),
        last(),
        map((incidents) =>
          incidents.filter((incident) => incident.itineraryId === itineraryId)
        )
      );
  }

  private sortIncidents(incidents: any[]) {
    const statusOrder: Record<string, number> = {
      [this.INCIDENT_STATUS.RESOLVED]: 1,
      [this.INCIDENT_STATUS.PENDING]: 2,
      [this.INCIDENT_STATUS.NOT_APPROVED]: 3,
      [this.INCIDENT_STATUS.APPROVED]: 4
    };

    return incidents.sort((a, b) => {
      const orderA = statusOrder[a.incidentStatus] ?? 99;
      const orderB = statusOrder[b.incidentStatus] ?? 99;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return (
        new Date(b.incidentDate).getTime() - new Date(a.incidentDate).getTime()
      );
    });
  }

  refreshIncidents() {
    this.isLoading = true;
    this.loadingMessage = 'Cargando observaciones pre-operativas...';
    this.fetchPreOperativeIncidents({ itineraryId: this.itinerario.id })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (incidents) => {
          this.incidents = this.sortIncidents(incidents);
          this.updateMigratedStatus();
        },
        error: (err) => {
          console.error('Error refreshing incidents:', err);
        }
      });
  }

  openAddPreOperativeIncident() {
    if (!this.usuario) {
      console.error('El usuario no está disponible');
      Swal.fire({
        position: 'center',
        icon: 'error',
        titleText: 'No se pudo obtener el usuario actual',
        text: 'Intente cerrar y volver a iniciar sesión, de persistir el problema contacte al TI.'
      });
      return;
    }
    this.isAddingObservation = true;
    // This could open the existing ReviewFileComponent to add an observation
    const dialogRef = this.dialog.open(DialogPreOperativeIncidentForm, {
      width: '600px',
      data: { itinerario: this.itinerario, usuario: this.usuario }
    });

    dialogRef.afterClosed().subscribe((newPreOperativeIncident) => {
      this.isAddingObservation = false;
      console.log('Dialog closed with data:', newPreOperativeIncident);
      if (newPreOperativeIncident) {
        this.incidents.unshift({
          id: newPreOperativeIncident.id,
          itineraryId: this.itinerario.id,
          incidentReason: newPreOperativeIncident.incidentReason,
          description: newPreOperativeIncident.description,
          destination: newPreOperativeIncident.destination,
          incidentDate: newPreOperativeIncident.incidentDate,
          deadline: newPreOperativeIncident.deadline,
          incidentStatus: this.INCIDENT_STATUS.PENDING
        });
      }
    });
  }

  updateIncidentStatus(incident: PreOperativeIncident, status: IncidentStatus) {
    this.isUpdating = true;
    this.cdRef.markForCheck();

    this.incidentService
      .updatePreOpIncident({
        incidentId: incident.id,
        itineraryId: this.itinerario.id,
        incidentStatus: status,
        modifiedBy: this.usuario
      })
      .pipe(
        finalize(() => {
          this.isUpdating = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response?.updateIncidenciaPreOperativa) {
            incident.incidentStatus = status;
            if (incident.incidentStatus === this.INCIDENT_STATUS.APPROVED) {
              incident.approvedBy = this.usuario;
            }
          }
          console.log('Incident status updated successfully:', response);
        },
        error: (error) => {
          console.error('Error updating incident status:', error);
        }
      });
  }

  deleteIncident(incident: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmDeleteObsDialogComponent, {
      width: '500px',
      panelClass: 'custom-dialog-container',
      data: {
        state: 'confirm',
        title: 'Antes de continuar...',
        message:
          'Si eliminas la observación agregada, esta también se eliminará de Sales App y no podrá resolverse.<br><br><b>Esta acción no se puede deshacer, ¿deseas continuar?</b>'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.incidentService
          .deletePreOpIncident({
            preOpIncidentId: incident.id,
            itineraryId: this.itinerario.id,
            deletedBy: this.usuario
          })
          .subscribe({
            next: () => {
              this.incidents.splice(index, 1);
              this.showFeedback('success', incident.incidentReason);
            },
            error: (err) => {
              console.error('Error al eliminar observación:', err);
              this.showFeedback('error', incident.incidentReason).subscribe(
                (retry) => {
                  if (retry) this.deleteIncident(incident, index);
                }
              );
            }
          });
      }
    });
  }

  showFeedback(state: 'success' | 'error', title: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteObsDialogComponent, {
      width: '500px',
      panelClass: 'custom-dialog-container',
      data: {
        state,
        title:
          state === 'success'
            ? 'Eliminado correctamente'
            : 'Error al eliminar observación',
        message:
          state === 'success'
            ? `Se eliminó la observación de "${title}" de Programación de Files y Listado de Itinerarios.`
            : `No se pudo eliminar la observación de ${title}.<br><br><b>Inténtalo nuevamente.</b>`
      }
    });
    return dialogRef.afterClosed();
  }

  close() {
    this.dialogRef.close(
      this.incidents.length > 0
        ? this.incidents.filter(
            (incident) =>
              incident.incidentStatus !== this.INCIDENT_STATUS.APPROVED
          ).length
        : null
    );
  }
}
